/**
 * Couleurs de marque Odys, source de vérité partagée entre les apps.
 * Consommées via la palette du thème (`theme.palette.primary.main`…) ;
 * n'importer ces constantes directement que hors contexte MUI.
 */
export const brandColors = {
  primary: "#1faba7",
  primaryLight: "#5cc7c4",
  /**
   * Teal foncé partagé (arbitrage P0.7, issue #2503) : pilote le hover des
   * boutons `contained` dans toutes les apps. Remplace le `#178a87` écrit en
   * dur dans le thème admin.
   */
  primaryDark: "#17807D",
  onPrimary: "#ffffff",
  /** Surface teintée teal (fonds de bloc, pistes de progression, chips). */
  primaryTint: "#e9f6f6",
  /**
   * Lavis teal, pour une surface ÉTENDUE : barre de navigation, bandeau pleine
   * largeur, en-tête de page. Distinct de `primaryTint`, qui est calibré pour
   * une petite surface — posé sur une bande présente sur toutes les pages, il
   * vire au teal franc et écrase le contenu (issue #2861).
   */
  primaryWash: "#f3fafa",
  /**
   * Teal lisible en texte et en icône POSÉ SUR `primaryTint`. Distinct de
   * `primaryDark` : celui-ci sert au hover des boutons, celui-là au contraste
   * du texte. C'est la confusion des deux rôles qui avait forcé odys à
   * surcharger `primary.dark` localement.
   */
  onPrimaryTint: "#0f6f6c",
  secondary: "#f0eeeb",
  secondaryDark: "#d9d7d4",
  onSecondary: "#1a1a1a",
  backgroundDefault: "#F9F8F7",
  backgroundPaper: "#ffffff",
  /** Arbitrage P0.3 (issue #2503) : la valeur la plus discrète des 4 apps gagne. */
  divider: "rgba(0,0,0,0.05)",
  /**
   * Gris de texte. Volontairement identiques aux défauts MUI : les tokeniser
   * ne change donc rien au rendu, mais donne aux apps une cible unique pour
   * remplacer leurs gris en dur (108 hex Softr dans odys, 3 hex dans admin).
   */
  textPrimary: "rgba(0,0,0,0.87)",
  textSecondary: "rgba(0,0,0,0.6)",
  textDisabled: "rgba(0,0,0,0.38)",
  /**
   * Couleurs sémantiques : les défauts MUI, promus en tokens pour qu'une app
   * n'ait plus à les recopier (onboarding en avait 19 recopiés à la main, et
   * avait dérivé sur `warning.main #ff9800`, qui est le `warning.light` de MUI).
   */
  error: "#d32f2f",
  errorLight: "#ef5350",
  errorDark: "#c62828",
  warning: "#ed6c02",
  warningLight: "#ff9800",
  warningDark: "#e65100",
  success: "#2e7d32",
  successLight: "#4caf50",
  successDark: "#1b5e20",
  info: "#0288d1",
  infoLight: "#03a9f4",
  infoDark: "#01579b",
  onSemantic: "#ffffff",
  /**
   * Bordures d'input. Deux crans seulement : repos et survol. Le focus prend
   * `primary`, l'erreur `error` — pilotés par MUI.
   */
  inputBorder: "rgba(0,0,0,0.12)",
  inputBorderHover: "rgba(0,0,0,0.24)",
} as const;

export type BrandColorToken = keyof typeof brandColors;

/**
 * Couleurs de marques tierces, pour un CTA qui envoie l'utilisateur chez elles. Jamais dans la
 * palette du thème : ce ne sont pas des couleurs Odys.
 */
export const partnerColors = {
  /** Vert Evaneos, repris du bouton « Send by Evaneos » de l'extension Chrome. */
  evaneos: "#003525",
  /** Vert foncé de l'icône Evaneos : le cadre de l'encart Evaneos des cartes de l'Odys AI panel. */
  evaneosIcon: "#00543C",
  /** Vert du bouton « Send by WhatsApp » de l'extension Chrome (emerald-700). */
  whatsapp: "#047857",
} as const;
